import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserInfo, UserInfoOrganization } from "services";
import {
  clearAccessToken,
  clearRefreshToken,
  setDefaultOrgId,
  saveOrgId,
  saveTenantCode,
  clearTenantCode,
  clearOrgId,
} from "utils";

import { initUserSessionAction } from "./thunks";

export interface AuthReducerState {
  status: "AUTH" | "UNAUTH";
  userProfile: UserInfo | null;
  userProfileInitializing: boolean;
  userProfileFetching: boolean;
  userPermissionsRefetching: boolean;
  userGroupPermissions: Record<string, string[]>;
  permissions: string[];
  orgSelected: UserInfoOrganization;
  userAppRole: "Admin" | "Employee";
}

const initialState: AuthReducerState = {
  status: "UNAUTH",
  userProfile: null,
  userProfileInitializing: false,
  userProfileFetching: false,
  userPermissionsRefetching: false,
  userGroupPermissions: {},
  permissions: [],
  orgSelected: {} as UserInfoOrganization,
  userAppRole: "Employee",
};

export const authSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    setOrgSelected: (
      state,
      action: PayloadAction<{ org?: UserInfoOrganization }>
    ) => {
      const orgSelected = action.payload.org;
      if (orgSelected) {
        state.orgSelected = orgSelected;
        setDefaultOrgId(Number(state.userProfile?.id), orgSelected?.id);
        saveOrgId(String(orgSelected?.id));
        saveTenantCode(String(orgSelected?.tenantCode));
        window.location.reload();
      }
    },
    logout: (state) => {
      clearAccessToken();
      clearRefreshToken();
      clearTenantCode();
      clearOrgId();
      state.status = "UNAUTH";
      state.userProfile = null;
      state.userProfileInitializing = false;
      state.userProfileFetching = false;
      state.userPermissionsRefetching = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initUserSessionAction.pending, (state) => {
      state.userProfileInitializing = true;
    });
    builder.addCase(
      initUserSessionAction.fulfilled,
      (
        state,
        action: PayloadAction<{
          user: UserInfo;
          orgSelected: UserInfoOrganization;
          userAppRole: string;
        }>
      ) => {
        state.userProfileInitializing = false;
        state.status = "AUTH";
        state.userProfile = action.payload.user;
        state.permissions = action.payload.user.permissions;
        state.orgSelected = action.payload.orgSelected;
        state.userAppRole = (action.payload.userAppRole as never) || "Employee";
      }
    );
    builder.addCase(initUserSessionAction.rejected, (state) => {
      state.userProfileInitializing = false;
      state.status = "UNAUTH";
      state.userProfile = null;
      state.userGroupPermissions = {};
      clearAccessToken();
      clearRefreshToken();
      clearTenantCode();
      clearOrgId();
    });
  },
});
